/**
 * An invite code held across sign-in.
 *
 * Someone who scans a team's QR while signed out has to sign in before they can join. The
 * OTP form keeps them on `/join/<code>`, but Google sign-in leaves the app and the server
 * brings everyone back to the home screen, so the code in the address bar is gone. Holding it
 * here for the length of the tab lets the app take them back to the invite they came for.
 *
 * `sessionStorage`: it survives the trip to Google and back in the same tab, and it does not
 * outlive the tab, so an invite never ambushes someone days later. Every access is guarded,
 * because private windows and blocked storage throw, and losing the code is no worse than
 * before this existed.
 */
import type { Destination, Session } from "./screen.ts";

const KEY = "sportsos.pendingInvite";

export function rememberInvite(code: string): void {
  try {
    window.sessionStorage.setItem(KEY, code);
  } catch {
    // Storage unavailable: the invite is simply not carried over.
  }
}

/** The held code, cleared as it is read, so it is acted on once. */
export function takeInvite(): string | null {
  try {
    const code = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
    return code === "" ? null : code;
  } catch {
    return null;
  }
}

// ── The decisions, kept apart from storage and React so they can be tested on their own ──

/** Signed in with an account. A guest cannot join a team, and the other states are not answers yet. */
export function isSignedIn(session: Session): boolean {
  return session !== "loading" && session !== "unreachable" && session !== "guest" && session !== null;
}

/**
 * The invite code to hold, or null. Only for someone who is on an invite page and cannot
 * join yet: signed out, a guest, or still being checked.
 */
export function inviteToRemember(
  signedIn: boolean,
  destination: Destination,
  inviteCode: string | null,
): string | null {
  return !signedIn && destination === "join_team" && inviteCode !== null ? inviteCode : null;
}

/**
 * Where to send someone who has just become signed in, given the invite that was held: the
 * invite's code, or null to leave them where they are. Null when nothing was held, and when
 * they are already on that invite, since sending them there again would be a pointless reload.
 */
export function inviteToReturnTo(
  destination: Destination,
  inviteCode: string | null,
  held: string | null,
): string | null {
  if (held === null) return null;
  if (destination === "join_team" && inviteCode === held) return null;
  return held;
}
